import React from 'react';

const StrategyPreview = ({ preview, onCreateAccount, onBack, onViewFull }) => {
  if (!preview) return null;

  const { avatar_preview, positioning_preview, sales_arguments_preview, content_teaser } = preview;

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="strategy-preview">
      {/* Success Header */}
      <header className="border-b border-gray-800 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/30 mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            Votre stratégie marketing est prête !
          </h1>
          <p className="text-gray-400 text-lg">
            Voici un aperçu de ce que l'IA a créé pour vous.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Preview Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          
          {/* Avatar Client Card */}
          <div className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden" data-testid="preview-avatar">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <span className="text-xl">👤</span>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Avatar Client</h3>
                  <p className="text-xs text-gray-500">Votre client idéal identifié</p>
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">Généré</span>
            </div>
            <div className="p-6">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                  {avatar_preview.name?.charAt(0) || 'C'}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white">{avatar_preview.name}</h4>
                  <p className="text-gray-400">{avatar_preview.age_range} • {avatar_preview.profession}</p>
                </div>
              </div>

              {/* Frustrations (partial) */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-400 mb-2">Frustrations principales</h5>
                <ul className="space-y-2">
                  {avatar_preview.frustrations?.map((f, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm text-gray-300">
                      <span className="text-red-400 mt-0.5">•</span>
                      <span>{f}</span>
                    </li>
                  ))}
                  <li className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="text-gray-700">•</span>
                    <span className="blur-sm">Frustration supplémentaire masquée...</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-800 text-gray-400 rounded-full">PRO</span>
                  </li>
                </ul>
              </div>

              {/* Desires (partial) */}
              <div className="mb-4">
                <h5 className="text-sm font-medium text-gray-400 mb-2">Désirs profonds</h5>
                <ul className="space-y-2">
                  {avatar_preview.desires?.map((d, i) => (
                    <li key={i} className="flex items-start space-x-2 text-sm text-gray-300">
                      <span className="text-green-400 mt-0.5">•</span>
                      <span>{d}</span>
                    </li>
                  ))}
                  <li className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="text-gray-700">•</span>
                    <span className="blur-sm">Désir supplémentaire masqué...</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-800 text-gray-400 rounded-full">PRO</span>
                  </li>
                </ul>
              </div>

              {/* Quote */}
              <div className="p-4 bg-gray-800/50 rounded-xl border-l-4 border-purple-500">
                <p className="text-gray-300 italic">"{avatar_preview.quote}"</p>
              </div>
            </div>
          </div>

          {/* Positioning Card */}
          <div className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden" data-testid="preview-positioning">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <span className="text-xl">🎯</span>
                </div>
                <div>
                  <h3 className="font-semibold text-white">Positionnement</h3>
                  <p className="text-xs text-gray-500">Votre place sur le marché</p>
                </div>
              </div>
              <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">Généré</span>
            </div>
            <div className="p-6">
              {/* Tagline */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-400 mb-2">Slogan</h5>
                <p className="text-2xl font-bold text-white">"{positioning_preview.tagline}"</p>
              </div>

              {/* USP */}
              <div className="mb-6">
                <h5 className="text-sm font-medium text-gray-400 mb-2">Proposition de valeur</h5>
                <p className="text-gray-300">{positioning_preview.unique_value_proposition}</p>
              </div>

              {/* Differentiators */}
              <div>
                <h5 className="text-sm font-medium text-gray-400 mb-3">Différenciateurs clés</h5>
                <div className="space-y-2">
                  {positioning_preview.key_differentiators?.map((d, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-xs text-blue-400">
                        {i + 1}
                      </div>
                      <span className="text-sm text-gray-300">{d}</span>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2 text-gray-600">
                    <div className="w-6 h-6 rounded-full bg-gray-800 flex items-center justify-center text-xs text-gray-500">
                      +
                    </div>
                    <span className="text-sm blur-sm">Différenciateurs supplémentaires...</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-800 text-gray-400 rounded-full">PRO</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Arguments */}
        <div className="bg-gray-900/50 rounded-2xl border border-gray-800 overflow-hidden mb-12" data-testid="preview-arguments">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <span className="text-xl">💡</span>
              </div>
              <div>
                <h3 className="font-semibold text-white">Arguments de Vente</h3>
                <p className="text-xs text-gray-500">Pour convaincre vos clients</p>
              </div>
            </div>
            <span className="text-xs text-gray-500">3 sur 5+ arguments</span>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-3 gap-4">
              {sales_arguments_preview?.map((arg, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white text-sm font-bold">
                      {i + 1}
                    </div>
                    <h4 className="font-semibold text-white text-sm">{arg.title}</h4>
                  </div>
                  <p className="text-sm text-gray-400">{arg.description}</p>
                </div>
              ))}
            </div>
            
            {/* Locked arguments */}
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {[4, 5].map((i) => (
                <div key={i} className="bg-gray-800/30 rounded-xl p-4 border border-gray-800 relative overflow-hidden">
                  <div className="absolute inset-0 backdrop-blur-sm bg-gray-900/50 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="w-8 h-8 text-gray-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      <span className="text-xs text-gray-500">Argument {i}</span>
                    </div>
                  </div>
                  <div className="opacity-30">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-700"></div>
                      <div className="h-4 w-24 bg-gray-700 rounded"></div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-gray-700 rounded"></div>
                      <div className="h-3 w-3/4 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content Teaser */}
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/20 p-8 mb-12" data-testid="preview-teaser">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-2">Contenus marketing prêts à utiliser</h3>
            <p className="text-gray-400">Créez un compte pour accéder à l'ensemble de votre stratégie</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: '📄', label: 'Page de vente', count: content_teaser?.sales_page || 1 },
              { icon: '✉️', label: 'Emails', count: content_teaser?.emails || 5 },
              { icon: '📱', label: 'Pubs Facebook', count: content_teaser?.facebook_ads || 3 },
              { icon: '🔍', label: 'Pubs Google', count: content_teaser?.google_ads || 3 },
              { icon: '📲', label: 'Posts Social', count: content_teaser?.social_posts || 10 },
              { icon: '📅', label: 'Calendrier', count: `${content_teaser?.calendar_days || 30}j` },
            ].map((item, i) => (
              <div key={i} className="bg-gray-900/50 rounded-xl p-4 text-center border border-gray-800 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-t from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <span className="text-2xl block mb-2">{item.icon}</span>
                <div className="text-xl font-bold text-green-400">{item.count}</div>
                <span className="text-xs text-gray-500">{item.label}</span>
                <div className="absolute top-2 right-2">
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Débloquez votre stratégie complète
            </h3>
            <p className="text-gray-400 mb-8">
              Créez un compte gratuit pour accéder à l'ensemble de vos contenus marketing, 
              les modifier et les exporter.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onCreateAccount}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-green-500/30 flex items-center justify-center space-x-2"
                data-testid="preview-cta-signup"
              >
                <span>Créer mon compte gratuit</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </button>
              <button
                onClick={onBack}
                className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl font-medium transition-all"
                data-testid="preview-cta-back"
              >
                Modifier ma stratégie
              </button>
            </div>

            {onViewFull && (
              <div className="mt-4">
                <button
                  onClick={onViewFull}
                  className="text-green-400 hover:text-green-300 text-sm underline"
                  data-testid="preview-view-full"
                >
                  Voir l'aperçu complet (limité)
                </button>
              </div>
            )}

            <p className="mt-6 text-xs text-gray-500">
              Déjà un compte ? <button className="text-green-400 hover:underline">Connectez-vous</button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default StrategyPreview;
